import { useQuery, useMutation, UseQueryOptions, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { surveyService } from '../services/api/surveyService';
import { SearchSurveysParams, SearchParticipantSurveysParams } from '../types/search-params';
import { 
  CreateSurveyRequest, 
  ArchiveSurveyRequest,
  UnarchiveSurveyRequest,
  BulkAddParticipantsData,
  BulkInviteParticipantsData
} from '../types/mutation-params';
import {
  Survey,
  SurveyIndicatorsResponse,
  SurveyApiResponse,
  SurveyNotificationData,
  SurveyListResponse,
  SurveySearchResponse,
  UpdateSurveyRequest,
  SurveySubmissionData,
  SurveyParticipant,
  NotificationHistoryResponse,
  ConsentRequestResponse,
  ConsentSendInclude,
  BulkAddParticipantsResponse,
  ParentAccountLinksResponse
} from '../types/survey';
import { surveySentryUtils } from '../features/survey/utils/sentry-integration';
import { createStandardizedError } from '../utils/errorUtils';

// Query keys for caching and invalidation
export const surveyKeys = {
  all: ['surveys'] as const,
  indicators: (v2: boolean) => [...surveyKeys.all, 'indicators', { v2 }] as const,
  surveyById: (id: string) => [...surveyKeys.all, 'survey', id] as const,
  survey: (id: string, limit?: number, offset?: number) => [...surveyKeys.all, 'survey', id, { limit, offset }] as const,
  notificationData: (surveyId: string) => [...surveyKeys.all, 'notificationData', surveyId] as const,
  responses: (participantId: string) => [...surveyKeys.all, 'responses', participantId] as const,
  search: (params: SearchSurveysParams) => [...surveyKeys.all, 'search', params] as const,
  participantSearch: (params: SearchParticipantSurveysParams) => [...surveyKeys.all, 'participantSearch', params] as const,
  notificationHistory: (surveyId: string, participantId: string) => [...surveyKeys.all, 'notificationHistory', surveyId, participantId] as const,
  parentAccountLinks: (surveyId: string) => [...surveyKeys.all, 'parentAccountLinks', surveyId] as const,
};

// Hook for getting survey indicators
export const useSurveyIndicators = (
  v2: boolean = true,
  includeQuestions: boolean = false,
  owner?: string,
  options: Omit<UseQueryOptions<SurveyIndicatorsResponse, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<SurveyIndicatorsResponse, Error>({
    queryKey: [...surveyKeys.indicators(v2), { includeQuestions, owner }],
    queryFn: () => surveyService.getSurveyIndicators(v2, includeQuestions, owner),
    staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Cache persists for 30 minutes
    ...options,
  });
};

// Hook for getting a specific survey
export const useSurvey = (
  surveyId: string,
  limit?: number,
  offset?: number,
  options: Omit<UseQueryOptions<Survey, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<Survey, Error>({
    queryKey: surveyKeys.survey(surveyId, limit, offset),
    queryFn: () => surveyService.getSurveyById(surveyId, limit, offset),
    enabled: !!surveyId,
    ...options,
  });
};

// Hook for creating a new survey
export const useCreateSurvey = (
  options: Omit<UseMutationOptions<SurveyApiResponse<Survey>, Error, CreateSurveyRequest>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<Survey>, Error, CreateSurveyRequest>({
    mutationFn: async (surveyData) => {
      // Track survey creation start
      surveySentryUtils.trackSurveyCreation(surveyData);
      
      try {
        const result = await surveyService.createSurvey(surveyData);
        
        // Track successful creation
        surveySentryUtils.addBreadcrumb('Survey created successfully', 'survey-action', 'info', {
          surveyId: result.data?.surveyId,
          surveyName: surveyData.surveyName,
        });
        
        return result;
      } catch (error) {
        // Track creation error
        surveySentryUtils.trackSurveyAPIError(
          error as Error,
          '/survey',
          undefined,
          { surveyName: surveyData.surveyName }
        );
        // Standardize error message from backend response
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, variables) => {
      console.log('[useCreateSurvey] Survey created successfully, invalidating queries...');
      
      // Invalidate all surveys-related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      console.log('[useCreateSurvey] Query invalidation completed');
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for updating a survey
export const useUpdateSurvey = (
  options: Omit<UseMutationOptions<SurveyApiResponse<Survey>, Error, UpdateSurveyRequest>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<Survey>, Error, UpdateSurveyRequest>({
    mutationFn: async (surveyData) => {
      try {
        return await surveyService.updateSurvey(surveyData as CreateSurveyRequest);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onMutate: async (newSurveyData) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: surveyKeys.survey(newSurveyData.survey.surveyId) });

      // Snapshot the previous value
      const previousSurvey = queryClient.getQueryData(surveyKeys.survey(newSurveyData.survey.surveyId));

      // Optimistically update to the new value
      queryClient.setQueryData(surveyKeys.survey(newSurveyData.survey.surveyId), (old: Survey | undefined) => {
        if (!old) return old;
        return {
          ...old,
          surveyName: newSurveyData.surveyName,
          semester: newSurveyData.survey.quarter,
          year: newSurveyData.survey.year,
          appliedTo: newSurveyData.survey.appliedTo,
          designatedRecipient: newSurveyData.survey.designatedRecipient,
          fixedOrder: newSurveyData.survey.fixedOrder,
          consentType: newSurveyData.consentType,
          consentTemplate: newSurveyData.consentTemplate,
          indicatorsV2: newSurveyData.survey.indicators.map((indicatorId: string) => ({
            indicatorId,
            // You might need to fetch indicator details here if needed
          })),
        };
      });

      // Return a context object with the snapshotted value
      return { previousSurvey };
    },
    onError: (err, newSurveyData, context: unknown) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context && typeof context === 'object' && 'previousSurvey' in context) {
        const typedContext = context as { previousSurvey?: Survey };
        if (typedContext.previousSurvey) {
          queryClient.setQueryData(surveyKeys.survey(newSurveyData.survey.surveyId), typedContext.previousSurvey);
        }
      }
      
      // Call the original onError if provided
      if (options.onError) {
        options.onError(err, newSurveyData, context);
      }
    },
    onSettled: (data, error, newSurveyData) => {
      // Always refetch after error or success to ensure we have the latest data
      // 
      // NOTE: Why we use optimistic updates instead of just invalidateQueries:
      // 
      // The previous approach of only using invalidateQueries() in onSuccess had a race condition:
      // 1. User submits form → onSuccess fires
      // 2. invalidateQueries() marks query as stale
      // 3. navigate() immediately called → new page loads
      // 4. useSurvey hook initializes with cached (old) data
      // 5. React Query doesn't automatically refetch because the component just mounted
      // 
      // This happens because:
      // - invalidateQueries() is asynchronous and doesn't guarantee immediate cache update
      // - Navigation happens before the invalidation is fully processed
      // - React Query shows cached data first when a component mounts, even if stale
      // - The new component doesn't trigger a refetch because it's a fresh mount
      // 
      // The optimistic update approach solves this by:
      // - Immediately updating the cache with new data (no race condition)
      // - Ensuring the new page shows updated data instantly
      // - Still invalidating as a safety net to ensure server consistency
      queryClient.invalidateQueries({ queryKey: surveyKeys.survey(newSurveyData.survey.surveyId) });
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSettled if provided
      if (options.onSettled) {
        options.onSettled(data, error, newSurveyData, undefined);
      }
    },
    onSuccess: (data, variables, context) => {
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    ...options,
  });
};

// Hook for deleting a survey
export const useDeleteSurvey = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, string>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, string>({
    mutationFn: async (surveyId) => {
      try {
        return await surveyService.deleteSurvey(surveyId);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, surveyId) => {
      // Invalidate all surveys-related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, surveyId, undefined);
      }
    },
    onError: (error, surveyId) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, surveyId, undefined);
      }
    },
    ...options,
  });
};

// Hook for updating survey attributes (consent template, etc.)
export const useUpdateSurveyAttributes = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, { surveyId: string; attributes: Record<string, unknown> }>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, { surveyId: string; attributes: Record<string, unknown> }>({
    mutationFn: async ({ surveyId, attributes }) => {
      try {
        return await surveyService.updateSurveyAttributes(surveyId, attributes);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, { surveyId }) => {
      // Invalidate and refetch survey data
      queryClient.invalidateQueries({ queryKey: surveyKeys.survey(surveyId) });
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, { surveyId, attributes: {} }, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for getting survey notification data
export const useSurveyNotificationData = (
  surveyId: string,
  options: Omit<UseQueryOptions<SurveyNotificationData, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<SurveyNotificationData, Error>({
    queryKey: surveyKeys.notificationData(surveyId),
    queryFn: () => surveyService.getSurveyNotificationData(surveyId),
    enabled: !!surveyId,
    ...options,
  });
};

// Hook for getting survey responses for a participant
export const useSurveyResponses = (
  participantId: string,
  options: Omit<UseQueryOptions<SurveySubmissionData, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<SurveySubmissionData, Error>({
    queryKey: surveyKeys.responses(participantId),
    queryFn: () => surveyService.getSurveyResponses(participantId),
    enabled: !!participantId,
    ...options,
  });
};

// Hook for closing a survey
export const useCloseSurvey = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, string>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, string>({
    mutationFn: async (surveyId) => {
      try {
        return await surveyService.closeSurvey(surveyId);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, surveyId) => {
      // Invalidate all surveys-related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, surveyId, undefined);
      }
    },
    onError: (error, surveyId) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, surveyId, undefined);
      }
    },
    ...options,
  });
};

// Hook for notifying a survey participant
export const useNotifyParticipant = (
  options: Omit<UseMutationOptions<unknown, Error, { participant: SurveyParticipant; surveyId: string }>, 'mutationFn'> = {}
) => {
  return useMutation<unknown, Error, { participant: SurveyParticipant; surveyId: string }>({
    mutationFn: async ({ participant, surveyId }) => {
      try {
        return await surveyService.notifyIndividualParticipant(participant, surveyId);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
};

// Hook for verifying a parent
export const useVerifyParent = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, Record<string, unknown>>, 'mutationFn'> = {}
) => {
  return useMutation<SurveyApiResponse<void>, Error, Record<string, unknown>>({
    mutationFn: async (verificationData) => {
      try {
        return await surveyService.verifyParent(verificationData);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
};

// Hook for saving participant responses
export const useSaveParticipantResponses = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, Record<string, unknown>>, 'mutationFn'> = {}
) => {
  return useMutation<SurveyApiResponse<void>, Error, Record<string, unknown>>({
    mutationFn: async (responseData) => {
      try {
        return await surveyService.saveParticipantResponses(responseData);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
};

// Hook for sending survey emails
export const useSendSurveyEmail = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, Record<string, unknown>>, 'mutationFn'> = {}
) => {
  return useMutation<SurveyApiResponse<void>, Error, Record<string, unknown>>({
    mutationFn: async (emailData) => {
      try {
        return await surveyService.sendSurveyEmail(emailData);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
};

// Hook for searching surveys by district or school
export const useSearchSurveys = (
  params: SearchSurveysParams,
  options: Omit<UseQueryOptions<SurveySearchResponse, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<SurveySearchResponse, Error>({
    queryKey: surveyKeys.search(params),
    queryFn: () => surveyService.searchSurveys(params),
    enabled: !!params.districtId || !!params.schoolId,
    staleTime: 0, // Always consider data stale to ensure fresh data after mutations
    gcTime: 1000 * 60 * 5, // Cache persists for 5 minutes
    ...options,
  });
};

// Hook for searching surveys by participant
export const useSearchParticipantSurveys = (
  params: SearchParticipantSurveysParams,
  options: Omit<UseQueryOptions<SurveyListResponse, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<SurveyListResponse, Error>({
    queryKey: surveyKeys.participantSearch(params),
    queryFn: () => surveyService.searchParticipantSurveys(params),
    enabled: !!(params.firstName && params.lastName) || !!params.studentId,
    ...options,
  });
};

// Hook for bulk adding participants to a survey
export const useBulkAddParticipants = (
  options: Omit<UseMutationOptions<BulkAddParticipantsResponse, Error, BulkAddParticipantsData>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<BulkAddParticipantsResponse, Error, BulkAddParticipantsData>({
    mutationFn: async ({ surveyId, participants }) => {
      // Track bulk participant addition
      surveySentryUtils.trackBulkOperation('participant addition', surveyId, participants.length);
      
      try {
        const result = await surveyService.bulkAddParticipants(surveyId, participants);
        
        // Track successful addition
        surveySentryUtils.addBreadcrumb('Participants added successfully', 'bulk-action', 'info', {
          surveyId,
          participantCount: participants.length,
        });
        
        return result;
      } catch (error) {
        // Track addition error
        surveySentryUtils.trackSurveyAPIError(
          error as Error,
          `/survey/${surveyId}/participants`,
          surveyId,
          { participantCount: participants.length }
        );
        // Standardize error message from backend response
        throw createStandardizedError(error);
      }
    },
    onMutate: async ({ surveyId, participants }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: surveyKeys.surveyById(surveyId) });

      // Snapshot all cached pages for this survey id.
      const previousSurveys = queryClient.getQueriesData<Survey>({
        queryKey: surveyKeys.surveyById(surveyId),
      });

      // Optimistically update all cached pages for this survey id.
      queryClient.setQueriesData(
        { queryKey: surveyKeys.surveyById(surveyId) },
        (old: Survey | undefined) => {
          if (!old) return old;
          return {
            ...old,
            participants: [
              ...(old.participants || []),
              ...participants.map((p: SurveyParticipant) => ({
                ...p,
                surveyParticipantId: `temp-${Date.now()}-${Math.random()}`,
                participantStatus: 'Not Started',
              })),
            ] as SurveyParticipant[],
          };
        }
      );

      // Return a context object with all snapshotted query values.
      return { previousSurveys };
    },
    onError: (err, { surveyId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context && typeof context === 'object' && 'previousSurveys' in context) {
        (context as { previousSurveys: Array<[readonly unknown[], Survey | undefined]> }).previousSurveys.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      }
      
      // Call the original onError if provided
      if (options.onError) {
        options.onError(err, { surveyId, participants: [] }, undefined);
      }
    },
    onSettled: (_, __, { surveyId }) => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: surveyKeys.surveyById(surveyId) });
    },
    onSuccess: (data, { surveyId }) => {
      // Also invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, { surveyId, participants: [] }, undefined);
      }
    },
    ...options,
  });
};

// Hook for bulk inviting participants to a survey
export const useBulkInviteParticipants = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, BulkInviteParticipantsData>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, BulkInviteParticipantsData>({
    mutationFn: async ({ surveyId, participantIds }) => {
      try {
        const result = await surveyService.bulkInviteParticipants(surveyId, participantIds);
        return result;
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, { surveyId }) => {
      // Invalidate both survey and participant queries to refresh invitation statuses
      queryClient.invalidateQueries({ queryKey: surveyKeys.survey(surveyId) });
      queryClient.invalidateQueries({ queryKey: ['participants', surveyId] });
      
      // Also invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, { surveyId, participantIds: [] }, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for getting notification data for raw data download
export const useGetNotificationData = (
  options: Omit<UseMutationOptions<SurveyApiResponse<SurveyNotificationData[]>, Error, string[]>, 'mutationFn'> = {}
) => {
  return useMutation<SurveyApiResponse<SurveyNotificationData[]>, Error, string[]>({
    mutationFn: async (surveyIds) => {
      try {
        const result = await surveyService.getNotificationData(surveyIds);
        return result;
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
};

// Hook for archiving surveys
export const useArchiveSurveys = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, ArchiveSurveyRequest>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, ArchiveSurveyRequest>({
    mutationFn: async (archiveData) => {
      try {
        return await surveyService.archiveSurveys(archiveData);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate all surveys-related queries to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: surveyKeys.all 
      });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for unarchiving surveys
export const useUnarchiveSurveys = (
  options: Omit<UseMutationOptions<SurveyApiResponse<void>, Error, UnarchiveSurveyRequest>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<SurveyApiResponse<void>, Error, UnarchiveSurveyRequest>({
    mutationFn: async (unarchiveData) => {
      try {
        return await surveyService.unarchiveSurveys(unarchiveData);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate all surveys-related queries to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: surveyKeys.all 
      });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for sending consent request (bulk)
export const useSendConsentRequest = (
  options: Omit<
    UseMutationOptions<
      ConsentRequestResponse,
      Error,
      { surveyId: string; include: ConsentSendInclude[] }
    >,
    'mutationFn'
  > = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<
    ConsentRequestResponse,
    Error,
    { surveyId: string; include: ConsentSendInclude[] }
  >({
    mutationFn: async ({ surveyId, include }) => {
      try {
        return await surveyService.sendConsentRequest(surveyId, { include });
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate survey data to refresh consent status
      queryClient.invalidateQueries({ queryKey: surveyKeys.survey(variables.surveyId) });
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
};

// Hook for sending consent request to individual participant
export const useSendIndividualConsentRequest = (
  options: Omit<UseMutationOptions<ConsentRequestResponse, Error, { surveyId: string; participantId: string }>, 'mutationFn'> = {}
) => {
  const queryClient = useQueryClient();
  
  return useMutation<ConsentRequestResponse, Error, { surveyId: string; participantId: string }>({
    mutationFn: async ({ surveyId, participantId }) => {
      try {
        return await surveyService.sendIndividualConsentRequest(surveyId, participantId);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    onSuccess: (data, { surveyId }) => {
      // Invalidate survey data to refresh consent status
      queryClient.invalidateQueries({ queryKey: surveyKeys.survey(surveyId) });
      queryClient.invalidateQueries({ queryKey: surveyKeys.all });
      
      // Also specifically invalidate search queries to ensure the survey list updates
      queryClient.invalidateQueries({ 
        queryKey: ['surveys', 'search']
      });
      
      // Call the original onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, { surveyId, participantId: '' }, undefined);
      }
    },
    onError: (error, variables) => {
      // Call the original onError if provided
      if (options.onError) {
        options.onError(error, variables, undefined);
      }
    },
    ...options,
  });
}; 

// Hook for getting notification history for a participant
export const useNotificationHistory = (
  surveyId: string,
  participantId: string,
  options: Omit<UseQueryOptions<NotificationHistoryResponse, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<NotificationHistoryResponse, Error>({
    queryKey: surveyKeys.notificationHistory(surveyId, participantId),
    queryFn: () => surveyService.getNotificationHistory(surveyId, participantId),
    enabled: !!surveyId && !!participantId,
    staleTime: 0, // Data is always considered stale
    gcTime: 0, // No cache persistence
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
};

/** On-demand parent account links for a survey (tm_admin Monitor Parent Links) */
export const useSurveyParentAccountLinks = (
  surveyId: string,
  options: Omit<UseQueryOptions<ParentAccountLinksResponse, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  const { enabled = true, ...rest } = options;
  return useQuery<ParentAccountLinksResponse, Error>({
    queryKey: surveyKeys.parentAccountLinks(surveyId),
    queryFn: () => surveyService.getSurveyParentAccountLinks(surveyId),
    staleTime: 1000 * 60 * 2,
    ...rest,
    enabled: !!surveyId && enabled,
  });
};

// Mutation for sending parent report to a single participant
export const useNotifyParent = (options?: Omit<UseMutationOptions<unknown, Error, { participant: SurveyParticipant; surveyId: string }>, 'mutationFn'>) => {
  return useMutation<unknown, Error, { participant: SurveyParticipant; surveyId: string }>({
    mutationFn: async ({ participant, surveyId }) => {
      try {
        return await surveyService.notifyParent(participant, surveyId);
      } catch (error) {
        throw createStandardizedError(error);
      }
    },
    ...options,
  });
}; 